import { useAtomValue } from "jotai";
import { type FunctionComponent, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { searchResultStreamAtom } from "scmp-app/components/search/atoms";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { SearchInfoTooltip } from "scmp-app/components/search/info-tooltip";
import type { searchExploreKeyAnglesArticleSearchResultStreamItemCategory$key } from "scmp-app/queries/__generated__/searchExploreKeyAnglesArticleSearchResultStreamItemCategory.graphql";

import { SearchExploreKeyAnglesItem } from "./item";
import {
  Container,
  HeaderContainer,
  ListContainer,
  NoResult,
  OuterContainer,
  StyledCircularProgress,
  StyledHeaderIcon,
  Title,
  TitleContainer,
} from "./styles";

type Props = {
  className?: string;
};

export const SearchExploreKeyAngles: FunctionComponent<Props> = ({ className }) => {
  const [expanded, setExpanded] = useState<number>();
  const { exploreKeyAngles, isReceivingExploreKeyAngles } = useAtomValue(searchResultStreamAtom);

  const searchResultStreamItems = useMemo(
    () => exploreKeyAngles.map(item => item.searchResultStream),
    [exploreKeyAngles],
  );
  const { currentValue } = useSearchContext();

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    setExpanded(undefined);
  }, [currentValue]);

  const items = useFragment<searchExploreKeyAnglesArticleSearchResultStreamItemCategory$key>(
    graphql`
      fragment searchExploreKeyAnglesArticleSearchResultStreamItemCategory on ArticleSearchResultStreamItemCategory
      @relay(plural: true) {
        id
        ...itemSearchExploreKeyAnglesItemArticleSearchResultStreamItemCategory
      }
    `,
    searchResultStreamItems,
  ).filter(notEmpty);

  const isShowLoading = useMemo(
    () => isReceivingExploreKeyAngles === undefined || isReceivingExploreKeyAngles === true,
    [isReceivingExploreKeyAngles],
  );

  const isNoResult = useMemo(
    () => isReceivingExploreKeyAngles === false && items.length === 0,
    [isReceivingExploreKeyAngles, items.length],
  );

  return (
    <OuterContainer className={className}>
      <Container>
        <HeaderContainer>
          <StyledHeaderIcon />
          <TitleContainer>
            <Title>Explore key angles</Title>
            <SearchInfoTooltip message="This AI-powered widget instantly organises search results by topic and key perspectives, giving you the essential context you need at a glance." />
          </TitleContainer>
        </HeaderContainer>
        {isNoResult ? (
          <NoResult>
            Try refining your search terms to uncover specific angles, or browse all results below.
          </NoResult>
        ) : (
          <ListContainer>
            {items?.map((item, index) => (
              <SearchExploreKeyAnglesItem
                expanded={expanded === index}
                index={index}
                key={item.id}
                onChange={setExpanded}
                reference={item}
              />
            ))}
          </ListContainer>
        )}
        {isShowLoading && <StyledCircularProgress size={30} />}
      </Container>
    </OuterContainer>
  );
};

SearchExploreKeyAngles.displayName = "SearchExploreKeyAngles";
